(() => {
  'use strict';
  const initial = document.querySelector('template[data-sac-crm-cart-state]');
  if (!(initial instanceof HTMLTemplateElement) || initial.dataset.bound === 'true') return;
  initial.dataset.bound = 'true';
  const sectionId = initial.dataset.sectionId;
  const rootPath = initial.dataset.root || '/';
  if (!/^[a-zA-Z0-9_-]+$/.test(sectionId)) return;
  const sectionUrl = new URL(rootPath.endsWith('/') ? rootPath : `${rootPath}/`, location.origin);
  sectionUrl.searchParams.set('sections', sectionId);
  const preview = new URLSearchParams(location.search).get('preview_theme_id');
  if (preview && /^\d+$/.test(preview)) sectionUrl.searchParams.set('preview_theme_id', preview);
  const panelSelector = '[data-sac-upcart-freight]';
  const checkoutSelector = '.upcart-checkout-button-container, .upcart-checkout-button, button[name="checkout"]';
  const watchSelector = '#upCart, #UpcartPopup, .upcart-footer';
  const holdStyles = {visibility: 'hidden', position: 'absolute', width: '0px', height: '0px', 'min-width': '0px', 'min-height': '0px', overflow: 'hidden', 'pointer-events': 'none'};
  const saved = new Map();
  let state = readState(initial);
  let shadowRoot, shadowObserver, host, controller, refreshTimer, probeTimer;
  let sequence = 0, probeRemaining = 0, domScheduled = false, suspended = false;

  function readState(template) {
    const flag = template?.dataset.checkoutBlocked;
    if (flag !== 'true' && flag !== 'false') throw new Error('Invalid freight state');
    if (flag === 'true' && !template.content.querySelector('[data-sac-upcart-freight-content] a[href]')) throw new Error('Missing freight quote');
    return {blocked: flag === 'true', content: template.content.cloneNode(true)};
  }
  function isCheckoutLink(element) {
    if (!(element instanceof HTMLAnchorElement)) return false;
    try { return /^\/checkouts?(?:\/|$)/.test(new URL(element.href, location.origin).pathname); }
    catch { return false; }
  }
  function hideCheckout(element) {
    if (!saved.has(element)) saved.set(element, {inert: element.inert, styles: Object.fromEntries(Object.keys(holdStyles).map(name => [name, {value: element.style.getPropertyValue(name), priority: element.style.getPropertyPriority(name)}]))});
    if (!element.inert) element.inert = true;
    for (const [name, value] of Object.entries(holdStyles)) if (element.style.getPropertyValue(name) !== value || element.style.getPropertyPriority(name) !== 'important') element.style.setProperty(name, value, 'important');
  }
  function restoreCheckout(element, previous) {
    element.inert = previous.inert;
    for (const [name, original] of Object.entries(previous.styles)) {
      if (element.style.getPropertyValue(name) !== holdStyles[name] || element.style.getPropertyPriority(name) !== 'important') continue;
      if (original.value) element.style.setProperty(name, original.value, original.priority);
      else element.style.removeProperty(name);
    }
    saved.delete(element);
  }
  function scheduleDom() {
    if (domScheduled || suspended) return;
    domScheduled = true;
    requestAnimationFrame(() => { domScheduled = false; if (!suspended) syncDom(); });
  }
  function selectRoot() {
    host = document.getElementById('upCart');
    const next = host?.shadowRoot;
    if (next !== shadowRoot) {
      shadowObserver?.disconnect();
      shadowRoot?.removeEventListener('click', stopFreightCheckout, true);
      shadowRoot = next;
      if (shadowRoot) {
        shadowObserver = new MutationObserver(scheduleDom);
        shadowObserver.observe(shadowRoot, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-disabled', 'style']});
        shadowRoot.addEventListener('click', stopFreightCheckout, true);
      }
    }
    if (shadowRoot) { clearTimeout(probeTimer); probeTimer = undefined; probeRemaining = 0; }
    return shadowRoot || document;
  }
  function syncDom() {
    const root = selectRoot();
    for (const [element, previous] of saved) if (!state.blocked || !element.isConnected) restoreCheckout(element, previous);
    const footer = root.querySelector('.upcart-footer');
    if (!footer) return;
    const existing = [...footer.querySelectorAll(panelSelector)];
    let panel = existing.shift();
    existing.forEach(element => element.remove());
    if (!state.blocked) { panel?.remove(); return; }
    for (const element of footer.querySelectorAll(checkoutSelector)) hideCheckout(element);
    for (const link of footer.querySelectorAll('a[href]')) if (isCheckoutLink(link)) hideCheckout(link);
    if (!panel) {
      panel = document.createElement('aside');
      panel.dataset.sacUpcartFreight = '';
      panel.setAttribute('role', 'status');
      panel.append(state.content.cloneNode(true));
      const style = document.createElement('style');
      style.textContent = '[data-sac-upcart-freight]{padding:14px;margin:10px 0;border:1px solid currentColor;border-radius:8px;font:inherit;color:inherit;background:transparent}[data-sac-upcart-freight] p{margin:8px 0}[data-sac-upcart-freight] a{display:block;padding:12px;border-radius:5px;background:#172123;color:#fff;text-align:center;text-decoration:none;font-weight:600}[data-sac-upcart-freight] a:focus-visible{outline:3px solid currentColor;outline-offset:3px}';
      panel.append(style);
      const wrapper = footer.querySelector('.upcart-checkout-button-container');
      if (wrapper) wrapper.after(panel);
      else footer.prepend(panel);
    }
  }
  function stopFreightCheckout(event) {
    if (!state.blocked) return;
    const target = event.composedPath().find(node => node instanceof Element && (node.matches('.upcart-checkout-button, button[name="checkout"]') || isCheckoutLink(node)));
    if (!target?.closest('.upcart-footer')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const root = selectRoot();
    syncDom();
    root.querySelector(`${panelSelector} a`)?.focus({preventScroll: true});
  }
  async function loadState() {
    refreshTimer = undefined;
    if (suspended) return;
    const requestSequence = ++sequence;
    controller?.abort();
    const requestController = new AbortController();
    controller = requestController;
    const timeout = setTimeout(() => requestController.abort(), 6000);
    try {
      const response = await fetch(sectionUrl.href, {method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}, signal: requestController.signal});
      if (!response.ok) throw new Error('Cart section unavailable');
      const payload = await response.json();
      const html = payload?.[sectionId];
      if (typeof html !== 'string' || html.length > 1000000) throw new Error('Invalid cart section');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const template = parsed.querySelector('template[data-sac-crm-cart-state]');
      if (!(template instanceof HTMLTemplateElement) || template.dataset.sectionId !== sectionId) throw new Error('Invalid section identity');
      const next = readState(template);
      if (requestSequence !== sequence || suspended) return;
      state = next;
      for (const root of [document, shadowRoot]) root?.querySelectorAll(panelSelector).forEach(panel => panel.remove());
      scheduleDom();
    } catch {
      if (requestSequence === sequence && !suspended) scheduleDom();
    } finally {
      clearTimeout(timeout);
      if (controller === requestController) controller = undefined;
    }
  }
  function probe() {
    if (shadowRoot || probeTimer || suspended) return;
    probeRemaining = 60;
    const step = () => {
      probeTimer = undefined;
      if (document.getElementById('upCart')?.shadowRoot) scheduleDom();
      else if (--probeRemaining > 0 && !suspended) probeTimer = setTimeout(step, 250);
    };
    probeTimer = setTimeout(step, 250);
  }
  function refresh() {
    if (suspended) return;
    ++sequence;
    controller?.abort();
    scheduleDom();
    probe();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(loadState, 60);
  }
  const discovery = new MutationObserver(records => {
    if (document.getElementById('upCart') !== host) { scheduleDom(); probe(); return; }
    if (records.some(record => record.target instanceof Element && (record.target.matches(watchSelector) || record.target.closest(watchSelector)) || [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element && (node.matches(watchSelector) || node.querySelector(watchSelector))))) scheduleDom();
  });
  discovery.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
  document.addEventListener('click', stopFreightCheckout, true);
  for (const name of ['aftersell-upcart:public-events:cart-loaded', 'aftersell-upcart:public-events:cart-updated', 'aftersell-upcart:public-events:cart-opened']) window.addEventListener(name, refresh);
  for (const name of ['on:cart:add', 'on:line-item:change', 'shopify:section:load']) document.addEventListener(name, refresh);
  window.addEventListener('pagehide', () => { suspended = true; ++sequence; controller?.abort(); clearTimeout(refreshTimer); clearTimeout(probeTimer); probeTimer = undefined; });
  window.addEventListener('pageshow', () => { suspended = false; refresh(); });
  syncDom();
  probe();
})();
